import { useState, useEffect, useContext } from 'react';
import { apiService, isAuthError } from './api';
import { ToastContext } from '../contexts/toast-context-provider';

// Refusals from raising a bill or starting a payment whose own message tells the
// student what happened and what to do; anything else stays a generic toast. These
// are also the codes a page reacts to - the bill page offers a way back to the order.
export const PAYMENT_REFUSAL_CODES = new Set([
  'ORDER_ALREADY_PAID',
  'ORDER_SUPERSEDED',
  'BILL_ALREADY_PAID',
  'BILL_PAYMENT_IN_PROGRESS',
  'INSTALMENT_PAYMENT_IN_PROGRESS',
  'ORDER_INSTALMENT_PLAN_SET',
  'BILL_BATCH_OUT_OF_DATE',
  'PAYMENT_ATTEMPTS_THROTTLED',
  'ERR_TRANSACTION_ALREADY_PAID',
  'ERR_TRANSACTION_ALREADY_REFUNDED',
]);

// Toasts what went wrong with a bill or a payment start, and hands the page back what
// it needs to react: the code, and the server's message when it is one worth showing.
function paymentRefusal(err, showToast) {
  if (isAuthError(err)) return { errorCode: err?.code || null, errorMessage: null };
  const known = PAYMENT_REFUSAL_CODES.has(err?.code);
  showToast(known ? err.message : 'Something went wrong!');
  return { errorCode: err?.code || null, errorMessage: known ? err.message : null };
}

const orderGateHook = {
  useGetOrdersDetails: ({ orderId, orgId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);
    const [refresh, setRefresh] = useState(0);

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await apiService.get(`view/order/${orderId}`, {}, { organization: orgId });
          setData(response);
        } catch (err) {
          if (!isAuthError(err)) showToast('Something went wrong!');
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [orderId, orgId, refresh]);

    return [data, loading, error, setRefresh];
  },
  useGetPrePaymentSummary: ({ preInvoiceId, orgId }) => {
    const [data, setData] = useState({
      prePaymentInvoice: {
        createdAt: null,
      },
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          // No skipAuthHandler: this hook now feeds only the bill page, which is
          // gated, so a 401 SHOULD hand the screen to the OTP gate. The
          // post-payment page has its own hook below, for the opposite reason.
          const response = await apiService.get(
            `view/pre-payment/${preInvoiceId}`,
            {},
            { organization: orgId },
          );
          setData(response);
        } catch (err) {
          if (!isAuthError(err)) showToast('Something went wrong!');
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [preInvoiceId, orgId]);

    return [data, loading, error];
  },
  // The post-payment status page's read. Separate from useGetPrePaymentSummary
  // because the two want opposite things from a 401: the bill page wants the
  // gate, and this page must never show a login form to somebody who has just
  // been charged. It calls the ungated /status route, which returns the invoice
  // status and the redirect keys and nothing else.
  useGetPaymentStatus: ({ preInvoiceId, orgId }) => {
    const [data, setData] = useState({
      prePaymentInvoice: { invoiceStatus: 'pending' },
      order: null,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await apiService.get(
            `view/pre-payment/${preInvoiceId}/status`,
            {},
            { organization: orgId },
            { skipAuthHandler: true },
          );
          setData(response);
        } catch (err) {
          // No toast either: the card renders its own "could not load status"
          // state, and a student staring at a payment result does not need a
          // second, vaguer message on top of it.
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [preInvoiceId, orgId]);

    return [data, loading, error];
  },
  useGetInvoiceDetails: ({ invoiceId, orgId }) => {
    const [data, setData] = useState({
      createdAt: null,
      costSplits: [],
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await apiService.get(`view/invoice/${invoiceId}`, {}, { organization: orgId });
          setData(response?.invoice);
        } catch (err) {
          if (!isAuthError(err)) showToast('Something went wrong!');
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [invoiceId, orgId]);

    return [data, loading, error];
  },
  useMakePreInvoice: () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    const makePreInvoice = async ({
      orderId,
      paymentType,
      createdBy,
      // discount,
      sellerState,
      buyerState,
      userMeta,
      // GST billing address, top-level (not inside userMeta): sent on a first
      // payment only; later instalments omit it and the server reuses the copy
      // committed to the order.
      billingAddress,
      lastUpdatedBy,
      paymentOption,
      instalmentPlanId,
      // The terms tick, and the element the checkout drew it against. The server
      // resolves which terms that means from the order itself and files the
      // acceptance; these only say whether a tick was given and whether the
      // buyer read the configured element or the static fallback line.
      tncAccepted,
      tncElementName,
      // eslint-disable-next-line consistent-return
    }) => {
      setLoading(true);
      try {
        return await apiService.post('api/orders/pre-payment-invoice/', {
          orderId,
          paymentType,
          createdBy,
          // discount,
          sellerState,
          buyerState,
          userMeta,
          ...(billingAddress ? { billingAddress } : {}),
          lastUpdatedBy,
          paymentOption,
          instalmentPlanId,
          tncAccepted,
          tncElementName,
        });
      } catch (err) {
        setError(err.message);
        return paymentRefusal(err, showToast);
      } finally {
        setLoading(false);
      }
    };
    return [loading, error, makePreInvoice];
  },
  useVisitCentre: () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    // Parks an offline-course order as a draft when the user opts to complete
    // enrolment at the centre instead of paying online.
    const visitCentre = async ({
      orderId, orgId, tncAccepted, tncElementName,
      // eslint-disable-next-line consistent-return
    }) => {
      setLoading(true);
      try {
        return await apiService.post(
          `view/order/${orderId}/visit-centre`,
          { tncAccepted, tncElementName },
          { organization: orgId },
        );
      } catch (err) {
        if (!isAuthError(err)) showToast('Something went wrong!');
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    return [loading, error, visitCentre];
  },
  // Batches offered for the course this draft is still waiting on, and the call that
  // chooses one. Both are scoped to the order server-side — the endpoint takes no
  // courseId — so neither can be used to browse batches for arbitrary courses.
  useOrderBatchSelection: () => {
    const [batches, setBatches] = useState([]);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const { showToast } = useContext(ToastContext);

    const loadBatchOptions = async ({ orderId }) => {
      setLoadingOptions(true);
      try {
        const response = await apiService.get(`view/order/${orderId}/batch-options`);
        setBatches(response?.batches || []);
      } catch (err) {
        setBatches([]);
        showToast('Could not load the available batches.');
      } finally {
        setLoadingOptions(false);
      }
    };

    // Posts a batch choice for this order. Resolves to true only when the order
    // actually moved on, so the caller knows whether to refetch.
    const postBatchChoice = async (path, { orderId, batchId }) => {
      setSubmitting(true);
      try {
        await apiService.post(`view/order/${orderId}/${path}`, { batchId });
        return true;
      } catch (err) {
        showToast(err?.message || 'Could not confirm that batch. Please try again.');
        return false;
      } finally {
        setSubmitting(false);
      }
    };

    // A draft waiting on its primary batch.
    const selectBatch = (choice) => postBatchChoice('select-batch', choice);

    // An order held because the student enrolled on a different batch than it was
    // for: settles it on the batch they meant, making it payable again.
    const confirmConflictBatch = (choice) => postBatchChoice('confirm-batch', choice);

    return {
      batches, loadingOptions, submitting, loadBatchOptions, selectBatch, confirmConflictBatch,
    };
  },
  useGetStatesList: ({ countryCode }) => {
    const [states, setStates] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    useEffect(() => {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await apiService.get(`view/states/${countryCode}`);
          setStates(response.states);
        } catch (err) {
          if (!isAuthError(err)) showToast('Something went wrong!');
          setError(err.message);
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [countryCode]);

    return [states, loading, error];
  },
  useMakePayment: () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    const makePayment = async ({
      prePaymentInvoiceId,
      deviceType,
      // eslint-disable-next-line consistent-return
    }) => {
      setLoading(true);
      try {
        return await apiService.post('api/orders/payments', {
          prePaymentInvoiceId,
          deviceType,
        });
      } catch (err) {
        setError(err.message);
        return paymentRefusal(err, showToast);
      } finally {
        setLoading(false);
      }
    };
    return [loading, error, makePayment];
  },
  getCoupons: ({ userId, orgId, skuId }) => {
    const [coupons, setCoupons] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
      const getCoupons = async () => {
        setLoading(true);
        try {
          if (!userId) {
            return;
          }
          const userCoupons = await apiService.get(`api/coupon/${orgId}/get-user-coupons/${userId}?skuId=${skuId}`);
          // console.log(userCoupons, 'userCoupons');
          setCoupons(userCoupons);
        } catch (err) {
          // console.log(err, 'error');
          setError(err);
        } finally {
          setLoading(false);
        }
      };
      getCoupons();
    }, [userId]);

    return [coupons, loading, error];
  },
  applyCoupon: () => {
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    const applyCoupon = async ({
      orgId,
      couponCode,
      accountId,
      orderId,
      // eslint-disable-next-line consistent-return
    }) => {
      setLoading(true);
      try {
        const result = await apiService.post('api/coupon/apply-coupon', {
          orgId,
          couponCode,
          accountId,
          orderId,
        });
        // console.log(result, 'resultt');
        setOrder(result);
        return result;
      } catch (err) {
        // console.log(err, err.message, 'error');
        if (!isAuthError(err)) {
          showToast(err.message || 'Something went wrong!');
        }
        setError(err.message);
        // console.log(err);
        return null;
      } finally {
        setLoading(false);
      }
    };
    return [loading, error, order, applyCoupon];
  },
  removeCoupon: () => {
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const { showToast } = useContext(ToastContext);

    const removeCoupon = async ({
      orgId,
      couponCode,
      accountId,
      orderId,
      // eslint-disable-next-line consistent-return
    }) => {
      setLoading(true);
      try {
        return await apiService.post('api/coupon/remove-coupon', {
          orgId,
          couponCode,
          accountId,
          orderId,
        });
      } catch (err) {
        if (!isAuthError(err)) showToast('Something went wrong!');
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    return [loading, error, removeCoupon];
  },
};

export default orderGateHook;
